import React, { useState, useMemo, useCallback, useEffect } from 'react';
import InfiniteScroll from 'react-infinite-scroller';

import type { MenuVariant, Product, Venue } from 'generated/graphql';
import { getFilteredMenuItems, getRandomLengthString } from 'utils';
import { apps } from 'utils/mockData';
import { MiniSearch } from 'components/Search/MiniSearch';
import { Alert } from 'components/Alerts/Alert';
import { IDENTIFIER_LOADING_PREFIX } from 'consts';
import { ListItem } from './ListItem';

import styles from './List.module.scss';

interface Props {
  listItems: Product[] | MenuVariant[];
  isLoading?: boolean;
  venue: Venue | undefined;
  hasBrands?: boolean;
}

export const getLoadingMenuItem = (index: number): Product => {
  return {
    mmId: `${IDENTIFIER_LOADING_PREFIX}${index || ''}`,
    imageUri: '',
    description: '',
    name: getRandomLengthString(10, 32),
    variablesByApp: [
      {
        app: apps[0],
        price: 1322,
        availability: 'available'
      },
      {
        app: apps[1],
        price: index && index % 2 ? 1321 : 1322,
        availability: 'available'
      }
    ],
    bundledItems: [],
    dietary: [],
    includedItems: [],
    menuDir: [],
    options: [],
    posId: `${IDENTIFIER_LOADING_PREFIX}${index || ''}`,
    surcounts: [],
    tags: []
  };
};

const pageSize = 6;

export const List: React.FC<Props> = ({ listItems, isLoading, venue, hasBrands = false }) => {
  const [searchValue, setSearchValue] = useState('');
  const [visibleResultCount, setVisibleResultCount] = useState(pageSize);
  const listItemTypeName = listItems[0]?.__typename;

  const _listItems = useMemo(() => {
    if (isLoading) {
      return Array.from({ length: 10 }, (_, index) => getLoadingMenuItem(index));
    }

    return listItems;
  }, [listItems, isLoading]);

  useEffect(() => setSearchValue(''), [listItemTypeName]);

  const loadMore = useCallback((offset: number) => {
    setVisibleResultCount((offset + 1) * pageSize);
  }, []);

  const filteredMenuItems = useMemo(() => {
    return getFilteredMenuItems(_listItems, searchValue.trim());
  }, [_listItems, searchValue]);

  const slicedResults = filteredMenuItems.slice(0, visibleResultCount);

  const alertResponses = {
    noResult: {
      title: 'No saucy searches found.',
      body: "We couldn't find any matches. Please try searching again."
    },
    noItems: {
      title: "Let's get cooking!",
      body: 'Once items are added to your menu, they’ll appear here.'
    }
  };

  return (
    <>
      <MiniSearch value={searchValue} onChange={setSearchValue} />
      {slicedResults.length ? (
        <div className={styles.List} data-testid="list">
          <InfiniteScroll
            pageStart={0}
            loadMore={loadMore}
            hasMore={visibleResultCount < filteredMenuItems.length}
            loader={<React.Fragment key="a" />}
            element="ul"
          >
            {slicedResults.map((item: Product | MenuVariant) => (
              <ListItem
                isLoading={isLoading}
                searchValue={searchValue}
                key={item.mmId}
                menuItem={item}
                venue={venue}
                hasBrands={hasBrands}
              />
            ))}
          </InfiniteScroll>
        </div>
      ) : (
        <Alert {...alertResponses[searchValue ? 'noResult' : 'noItems']} />
      )}
    </>
  );
};
